<template>
    <div class="chart-display-wrapper">
        <!-- Barre d'outils (paramétrage inline) -->
        <div v-if="editable || isLineChart || (rows && rows.length > 0)" class="chart-display-toolbar">
            <b-button v-if="editable" size="sm" variant="outline-secondary"
                @click="showConfigPanel = !showConfigPanel">
                <i :class="showConfigPanel ? 'fas fa-times' : 'fas fa-sliders-h'"></i>
                {{ showConfigPanel ? 'Fermer' : 'Paramètres' }}
            </b-button>
            <b-button v-if="editable" size="sm" variant="outline-secondary" @click="openConfigLoadModal">
                <i class="fas fa-folder-open"></i> Charger
            </b-button>
            <b-button v-if="isLineChart" size="sm" variant="outline-secondary" @click="resetZoom">
                <i class="fas fa-search-minus"></i> Réinitialiser zoom
            </b-button>
            <b-button v-if="rows && rows.length > 0" size="sm" variant="outline-secondary" @click="downloadRawCsv">
                <i class="fas fa-download"></i> Données brutes (CSV)
            </b-button>
            <b-button v-if="rows && rows.length > 0" size="sm" variant="outline-secondary" @click="downloadCsv">
                <i class="fas fa-chart-bar"></i> Données graphique (CSV)
            </b-button>
            <b-button v-if="rows && rows.length > 0" size="sm" variant="outline-secondary" @click="downloadImage">
                <i class="fas fa-image"></i> Image (PNG)
            </b-button>
        </div>

        <!-- Panel de configuration inline -->
        <b-collapse :visible="showConfigPanel">
            <div class="chart-inline-config p-2 mb-2 border rounded">
                <chart-config-form ref="chartConfigForm" :value="localConfig" :columns="rowColumns" :table-structure="tableStructure" :context="tableName" @input="onLocalConfigInput" />
            </div>
        </b-collapse>

        <!-- Texte avant le graphique -->
        <div v-if="localConfig.pre_text" class="chart-pre-text">{{ localConfig.pre_text }}</div>

        <!-- Graphique -->
        <div class="chart-display-container" :class="{ 'chart-display-container--line': isLineChart }">
            <p v-if="!rows || rows.length === 0" class="text-muted">Aucun résultat.</p>
            <canvas v-else ref="canvas"></canvas>
        </div>

        <!-- Légende HTML externe (pie uniquement) -->
        <div v-if="pieLegendItems.length > 0" class="chart-html-legend">
            <span v-for="(item, i) in pieLegendItems" :key="i" class="chart-legend-item">
                <span class="chart-legend-swatch" :style="{ background: item.fillStyle, borderColor: item.strokeStyle }"></span>
                <span class="chart-legend-label">{{ item.text }}</span>
            </span>
        </div>
    </div>
</template>

<script>
import Chart from 'chart.js/auto';
import zoomPlugin from 'chartjs-plugin-zoom';
import 'chartjs-adapter-date-fns';
import ChartConfigForm from './ChartConfigForm.vue';

Chart.register(zoomPlugin);

const COLORS = [
    '#4e79a7','#f28e2b','#e15759','#76b7b2','#59a14f',
    '#edc948','#b07aa1','#ff9da7','#9c755f','#bab0ac',
    '#1f77b4','#ff7f0e','#2ca02c','#d62728','#9467bd',
    '#8c564b','#e377c2','#7f7f7f','#bcbd22','#17becf'
];

function truncDate(val, unit) {
    const d = new Date(val);
    if (isNaN(d.getTime())) return val;
    const pad = n => String(n).padStart(2, '0');
    const y = d.getFullYear();
    const mo = pad(d.getMonth() + 1);
    const da = pad(d.getDate());
    const h  = pad(d.getHours());
    const mi = pad(d.getMinutes());
    // Retourne toujours une ISO string pour que l'axe time de Chart.js puisse parser
    if (unit === 'minute') return `${y}-${mo}-${da}T${h}:${mi}:00`;
    if (unit === 'hour')   return `${y}-${mo}-${da}T${h}:00:00`;
    if (unit === 'year')   return `${y}-01-01`;
    if (unit === 'month')  return `${y}-${mo}-01`;
    return `${y}-${mo}-${da}`;
}

// Ajoute des points à 0 juste avant et juste après chaque groupe de données,
// ce qui donne visuellement la descente à zéro sans remplir tous les intervalles.
function fillTimeGaps(agg, unit) {
    const keys = Object.keys(agg).sort();
    if (keys.length === 0) return agg;

    const stepMs = { minute: 60000, hour: 3600000, day: 86400000, month: null, year: null };

    function addStep(isoKey, n) {
        const d = new Date(isoKey);
        if (unit === 'minute') d.setMinutes(d.getMinutes() + n);
        else if (unit === 'hour')  d.setHours(d.getHours() + n);
        else if (unit === 'day')   d.setDate(d.getDate() + n);
        else if (unit === 'month') d.setMonth(d.getMonth() + n);
        else if (unit === 'year')  d.setFullYear(d.getFullYear() + n);
        const pad = x => String(x).padStart(2, '0');
        const y = d.getFullYear(), mo = pad(d.getMonth()+1), da = pad(d.getDate());
        const h = pad(d.getHours()), mi = pad(d.getMinutes());
        if (unit === 'minute') return `${y}-${mo}-${da}T${h}:${mi}:00`;
        if (unit === 'hour')   return `${y}-${mo}-${da}T${h}:00:00`;
        if (unit === 'year')   return `${y}-01-01`;
        if (unit === 'month')  return `${y}-${mo}-01`;
        return `${y}-${mo}-${da}`;
    }

    function isOneStep(a, b) {
        return addStep(a, 1) === b;
    }

    const result = { ...agg };

    // Pour chaque gap entre deux points consécutifs, ajouter un 0 après et un 0 avant
    for (let i = 0; i < keys.length - 1; i++) {
        const cur  = keys[i];
        const next = keys[i + 1];
        if (!isOneStep(cur, next)) {
            const afterCur   = addStep(cur, 1);
            const beforeNext = addStep(next, -1);
            if (!(afterCur in result))   result[afterCur]   = 0;
            if (!(beforeNext in result)) result[beforeNext] = 0;
        }
    }


    return result;
}

function applyTransform(rows, cfg) {
    if (!cfg.transform_code) return rows;
    try {
        /* eslint-disable no-new-func */
        const fn = new Function('data', cfg.transform_code);
        if ((cfg.transform_mode || 'row') === 'all') {
            const cloned = JSON.parse(JSON.stringify(rows));
            const result = fn(cloned);
            return Array.isArray(result) ? result : cloned;
        } else {
            const cloned = JSON.parse(JSON.stringify(rows));
            cloned.forEach(row => fn(row));
            return cloned;
        }
    } catch (e) {
        console.warn('[ChartDisplay] Erreur transform:', e);
        return rows;
    }
}

function buildChartData(rows, cfg) {
    const chartType = cfg.type || 'pie';
    const labelCol  = cfg.label_col  || (rows[0] ? Object.keys(rows[0])[0] : '');
    const dataMode  = cfg.data_mode  || 'count';
    const seriesCol = cfg.series_col || '';
    const doTrunc   = cfg.date_trunc;
    const truncUnit = cfg.date_trunc_unit || 'day';
    const tension   = cfg.smooth !== false ? 0.3 : 0;
    const isArea   = (chartType === 'area');
    const isColumn = (chartType === 'column');
    const isPolar  = (chartType === 'polar');
    const isRadar  = (chartType === 'radar');
    const isBubble = (chartType === 'bubble');
    const isPie     = (chartType === 'pie' || chartType === 'chart_pie');
    const isLine    = !isPie && !isPolar && !isRadar && !isBubble;
    // Quand truncate est actif sur un line chart, on utilise l'axe time natif de Chart.js
    const useTimeScale = isLine && !!doTrunc;

    let labels, datasets;

    // ── Polar Area ─────────────────────────────────────────────────────────────
    if (isPolar) {
        const valueCol = cfg.value_col || (rows[0] ? Object.keys(rows[0])[1] : '');
        const agg = {};
        for (const row of rows) {
            const raw = row[labelCol] != null ? String(row[labelCol]) : '(vide)';
            const key = doTrunc ? truncDate(raw, truncUnit) : raw;
            if (dataMode === 'sum') {
                agg[key] = (agg[key] || 0) + (parseFloat(row[valueCol]) || 0);
            } else if (dataMode === 'count') {
                agg[key] = (agg[key] || 0) + 1;
            } else {
                agg[key] = parseFloat(row[valueCol]) || 0;
            }
        }
        const entries = Object.entries(agg).sort((a, b) => b[1] - a[1]);
        labels = entries.map(e => e[0]);
        const values = entries.map(e => e[1]);
        datasets = [{ data: values, backgroundColor: COLORS.slice(0, values.length).map(c => c + 'aa') }];
        return { labels, datasets, isPie: false, isPolar: true, isRadar: false, isBubble: false, isColumn: false, isArea: false, useTimeScale: false, timeUnit: truncUnit };
    }

    // ── Radar ──────────────────────────────────────────────────────────────────
    if (isRadar) {
        const valueCol  = cfg.value_col  || (rows[0] ? Object.keys(rows[0])[1] : '');
        if (seriesCol) {
            // Multi-séries : chaque valeur de seriesCol = une toile
            const seriesMap = {};
            const allLabelsSet = new Set();
            for (const row of rows) {
                const raw = row[labelCol] != null ? String(row[labelCol]) : '(vide)';
                const key = doTrunc ? truncDate(raw, truncUnit) : raw;
                const sv  = row[seriesCol] != null ? String(row[seriesCol]) : '(vide)';
                if (!seriesMap[sv]) seriesMap[sv] = {};
                if (dataMode === 'sum') {
                    seriesMap[sv][key] = (seriesMap[sv][key] || 0) + (parseFloat(row[valueCol]) || 0);
                } else if (dataMode === 'count') {
                    seriesMap[sv][key] = (seriesMap[sv][key] || 0) + 1;
                } else {
                    seriesMap[sv][key] = parseFloat(row[valueCol]) || 0;
                }
                allLabelsSet.add(key);
            }
            labels = Array.from(allLabelsSet).sort();
            const sortedNames = Object.keys(seriesMap).sort();
            datasets = sortedNames.map((sName, i) => ({
                label: sName,
                data: labels.map(l => seriesMap[sName][l] || 0),
                borderColor: COLORS[i % COLORS.length],
                backgroundColor: COLORS[i % COLORS.length] + '44',
                fill: true,
                pointBackgroundColor: COLORS[i % COLORS.length]
            }));
        } else {
            // Série unique
            const agg = {};
            for (const row of rows) {
                const raw = row[labelCol] != null ? String(row[labelCol]) : '(vide)';
                const key = doTrunc ? truncDate(raw, truncUnit) : raw;
                if (dataMode === 'sum') {
                    agg[key] = (agg[key] || 0) + (parseFloat(row[valueCol]) || 0);
                } else if (dataMode === 'count') {
                    agg[key] = (agg[key] || 0) + 1;
                } else {
                    agg[key] = parseFloat(row[valueCol]) || 0;
                }
            }
            labels = Object.keys(agg).sort();
            datasets = [{
                label: cfg.title || valueCol,
                data: labels.map(l => agg[l]),
                borderColor: COLORS[0],
                backgroundColor: COLORS[0] + '44',
                fill: true,
                pointBackgroundColor: COLORS[0]
            }];
        }
        return { labels, datasets, isPie: false, isPolar: false, isRadar: true, isBubble: false, isColumn: false, isArea: false, useTimeScale: false, timeUnit: truncUnit };
    }

    // ── Bubble ─────────────────────────────────────────────────────────────────
    if (isBubble) {
        const valueCol       = cfg.value_col       || (rows[0] ? Object.keys(rows[0])[1] : '');
        const radiusCol      = cfg.radius_col      || valueCol;
        const bubbleLabelCol = cfg.bubble_label_col || '';
        const makePoint = (row) => ({
            x: parseFloat(row[labelCol]) || 0,
            y: parseFloat(row[valueCol]) || 0,
            r: Math.max(3, Math.min(40, parseFloat(row[radiusCol]) || 5)),
            ...(bubbleLabelCol && row[bubbleLabelCol] != null ? { pointLabel: String(row[bubbleLabelCol]) } : {})
        });
        if (seriesCol) {
            const seriesMap = {};
            for (const row of rows) {
                const sv = row[seriesCol] != null ? String(row[seriesCol]) : '(vide)';
                if (!seriesMap[sv]) seriesMap[sv] = [];
                seriesMap[sv].push(makePoint(row));
            }
            datasets = Object.keys(seriesMap).sort().map((sName, i) => ({
                label: sName,
                data: seriesMap[sName],
                backgroundColor: COLORS[i % COLORS.length] + 'aa',
                borderColor: COLORS[i % COLORS.length]
            }));
        } else {
            datasets = [{
                label: cfg.title || 'Données',
                data: rows.map(makePoint),
                backgroundColor: COLORS[0] + 'aa',
                borderColor: COLORS[0]
            }];
        }
        return { labels: [], datasets, isPie: false, isPolar: false, isRadar: false, isBubble: true, isColumn: false, isArea: false, useTimeScale: false, timeUnit: truncUnit };
    }

    // ── Multi-séries (courbe seulement) ────────────────────────────────────────
    if (isLine && seriesCol) {
        if (dataMode === 'count' || dataMode === 'sum') {
            const valueCol  = cfg.value_col || '';
            const seriesMap = {};
            const allLabelsSet = new Set();
            for (const row of rows) {
                const raw = row[labelCol] != null ? String(row[labelCol]) : '(vide)';
                const key = doTrunc ? truncDate(raw, truncUnit) : raw;
                const sv  = row[seriesCol] != null ? String(row[seriesCol]) : '(vide)';
                if (!seriesMap[sv]) seriesMap[sv] = {};
                if (dataMode === 'sum') {
                    seriesMap[sv][key] = (seriesMap[sv][key] || 0) + (parseFloat(row[valueCol]) || 0);
                } else {
                    seriesMap[sv][key] = (seriesMap[sv][key] || 0) + 1;
                }
                allLabelsSet.add(key);
            }
            const sortedLabels = Array.from(allLabelsSet).sort();
            if (useTimeScale) {
                labels = [];
                // Union de tous les x, avec null pour les valeurs manquantes (pas de faux zéros)
                datasets = Object.keys(seriesMap).sort().map((sName, i) => {
                    const pts = sortedLabels.map(l => ({ x: l, y: seriesMap[sName][l] != null ? seriesMap[sName][l] : 0 }));
                    return {
                        label: sName,
                        data: pts,
                        borderColor: COLORS[i % COLORS.length],
                        backgroundColor: isColumn ? COLORS[i % COLORS.length] : (isArea ? COLORS[i % COLORS.length] + '55' : 'transparent'),
                        tension, fill: isArea ? (!!cfg.stacked ? 'stack' : 'origin') : false,
                        pointRadius: pts.length > 200 ? 0 : 3, pointHoverRadius: 5
                    };
                });
            } else {
                labels = sortedLabels;
                datasets = Object.keys(seriesMap).sort().map((sName, i) => ({
                    label: sName,
                    data: labels.map(l => seriesMap[sName][l] || 0),
                    borderColor: COLORS[i % COLORS.length],
                    backgroundColor: isColumn ? COLORS[i % COLORS.length] : (isArea ? COLORS[i % COLORS.length] + '55' : 'transparent'),
                    tension, fill: isArea ? (!!cfg.stacked ? 'stack' : 'origin') : false,
                    pointRadius: rows.length > 200 ? 0 : 3, pointHoverRadius: 5
                }));
            }
        } else {
            const valueCol = cfg.value_col || (rows[0] ? Object.keys(rows[0])[1] : '');
            const seriesMap = {};
            const allLabelsSet = new Set();
            for (const row of rows) {
                const key = row[labelCol] != null ? String(row[labelCol]) : '';
                const sv  = row[seriesCol] != null ? String(row[seriesCol]) : '(vide)';
                if (!seriesMap[sv]) seriesMap[sv] = {};
                seriesMap[sv][key] = parseFloat(row[valueCol]) || 0;
                allLabelsSet.add(key);
            }
            const sortedLabels = Array.from(allLabelsSet).sort();
            if (useTimeScale) {
                labels = [];
                datasets = Object.keys(seriesMap).sort().map((sName, i) => ({
                    label: sName,
                    data: sortedLabels.map(l => ({ x: l, y: seriesMap[sName][l] != null ? seriesMap[sName][l] : 0 })),
                    borderColor: COLORS[i % COLORS.length],
                    backgroundColor: isColumn ? COLORS[i % COLORS.length] : (isArea ? COLORS[i % COLORS.length] + '55' : 'transparent'),
                    tension, fill: isArea ? (!!cfg.stacked ? 'stack' : 'origin') : false,
                    pointRadius: rows.length > 200 ? 0 : 3, pointHoverRadius: 5
                }));
            } else {
                labels = sortedLabels;
                datasets = Object.keys(seriesMap).sort().map((sName, i) => ({
                    label: sName,
                    data: labels.map(l => seriesMap[sName][l] || 0),
                    borderColor: COLORS[i % COLORS.length],
                    backgroundColor: isColumn ? COLORS[i % COLORS.length] : (isArea ? COLORS[i % COLORS.length] + '55' : 'transparent'),
                    tension, fill: isArea ? (!!cfg.stacked ? 'stack' : 'origin') : false,
                    pointRadius: rows.length > 200 ? 0 : 3, pointHoverRadius: 5
                }));
            }
        }

    // ── Série unique ────────────────────────────────────────────────────────────
    } else {
        let values;
        if (dataMode === 'count' || dataMode === 'sum') {
            const valueCol = cfg.value_col || '';
            const agg = {};
            for (const row of rows) {
                const raw = row[labelCol] != null ? String(row[labelCol]) : '(vide)';
                const key = doTrunc ? truncDate(raw, truncUnit) : raw;
                if (dataMode === 'sum') {
                    agg[key] = (agg[key] || 0) + (parseFloat(row[valueCol]) || 0);
                } else {
                    agg[key] = (agg[key] || 0) + 1;
                }
            }
            const entries = Object.entries(agg);
            if (isPie) entries.sort((a, b) => b[1] - a[1]);
            else entries.sort((a, b) => (a[0] < b[0] ? -1 : 1));

            // Camembert : regrouper les tranches au-delà de PIE_MAX_SLICES en "Autres"
            const PIE_MAX_SLICES = cfg.pie_max_slices > 1 ? cfg.pie_max_slices : 15;
            if (isPie && entries.length > PIE_MAX_SLICES) {
                const main  = entries.slice(0, PIE_MAX_SLICES - 1);
                const rest  = entries.slice(PIE_MAX_SLICES - 1);
                const other = rest.reduce((sum, e) => sum + e[1], 0);
                main.push(['Autres (' + rest.length + ')', other]);
                entries.length = 0;
                main.forEach(e => entries.push(e));
            }
            if (useTimeScale) {
                const filled = fillTimeGaps(Object.fromEntries(entries), truncUnit);
                const filledEntries = Object.entries(filled).sort((a, b) => a[0] < b[0] ? -1 : 1);
                labels = filledEntries.map(e => e[0]);
                values = filledEntries.map(e => e[1]);
            } else {
                labels = entries.map(e => e[0]);
                values = entries.map(e => e[1]);
            }
        } else {
            const valueCol = cfg.value_col || (rows[0] ? Object.keys(rows[0])[1] : '');
            labels = rows.map(r => r[labelCol] != null ? String(r[labelCol]) : '');
            values = rows.map(r => parseFloat(r[valueCol]) || 0);
        }

        const seriesLabel = cfg.title || (dataMode === 'count' ? 'Nombre' : (cfg.value_col || ''));

        if (isPie) {
            datasets = [{ data: values, backgroundColor: COLORS.slice(0, values.length) }];
        } else if (isColumn) {
            const barColors = values.map((_, i) => COLORS[i % COLORS.length]);
            if (useTimeScale) {
                datasets = [{
                    label: seriesLabel,
                    data: labels.map((l, i) => ({ x: l, y: values[i] })),
                    backgroundColor: barColors,
                    borderColor: barColors,
                    borderWidth: 1
                }];
                labels = [];
            } else {
                datasets = [{
                    label: seriesLabel,
                    data: values,
                    backgroundColor: barColors,
                    borderColor: barColors,
                    borderWidth: 1
                }];
            }
        } else if (useTimeScale) {
            datasets = [{
                label: seriesLabel,
                data: labels.map((l, i) => ({ x: l, y: values[i] })),
                borderColor: '#4e79a7',
                backgroundColor: isArea ? 'rgba(78,121,167,0.40)' : 'rgba(78,121,167,0.15)',
                tension, fill: true,
                pointRadius: rows.length > 60 ? 0 : 3, pointHoverRadius: 5
            }];
            labels = [];
        } else {
            datasets = [{
                label: seriesLabel,
                data: values,
                borderColor: '#4e79a7',
                backgroundColor: isArea ? 'rgba(78,121,167,0.40)' : 'rgba(78,121,167,0.15)',
                tension, fill: true,
                pointRadius: rows.length > 60 ? 0 : 3, pointHoverRadius: 5
            }];
        }
    }

    return { labels, datasets, isPie, isPolar: false, isRadar: false, isBubble: false, isColumn, isArea, useTimeScale, timeUnit: truncUnit };
}

export default {
    name: 'ChartDisplay',
    components: { ChartConfigForm },
    props: {
        rows:            { type: Array,   required: true },
        config:          { type: Object,  required: true },
        editable:        { type: Boolean, default: false },
        tableStructure:  { type: Array,   default: null },
        tableName:       { type: String,  default: null },
        autoLoadEnabled: { type: Boolean, default: true }
    },
    data() {
        return {
            chartInstance:   null,
            localConfig:     { ...this.config },
            showConfigPanel: false,
            pieLegendItems:  []
        };
    },
    computed: {
        rowColumns() {
            return (this.rows && this.rows.length > 0) ? Object.keys(this.rows[0]) : [];
        },
        isLineChart() {
            const t = this.localConfig.type || 'pie';
            return t !== 'pie' && t !== 'chart_pie';
        }
    },
    watch: {
        config: {
            deep: true,
            handler(val) {
                this.localConfig = { ...val };
            }
        },
        rows: 'renderChart',
        localConfig: { deep: true, handler: 'renderChart' },
        tableName(newVal, oldVal) {
            if (newVal !== oldVal) this.$nextTick(() => {
                this.resetConfig();
                if (this.autoLoadEnabled) this.autoLoad();
            });
        }
    },
    mounted() {
        this.$nextTick(() => {
            this.renderChart();
            if (this.autoLoadEnabled) this.autoLoad();
        });
    },
    beforeDestroy() {
        this.destroyChart();
    },
    methods: {
        autoLoad() {
            if (this.$refs.chartConfigForm) return this.$refs.chartConfigForm.autoLoad();
        },
        resetConfig() {
            if (this.$refs.chartConfigForm) this.$refs.chartConfigForm.resetConfig();
        },
        onLocalConfigInput(cfg) {
            this.localConfig = cfg;
            this.$emit('config-change', cfg);
        },
        openConfigLoadModal() {
            this.$refs.chartConfigForm && this.$refs.chartConfigForm.openLoadModal();
        },

        resetZoom() {
            if (this.chartInstance) this.chartInstance.resetZoom();
        },
        downloadRawCsv() {
            if (!this.rows || this.rows.length === 0) return;
            const transformedRows = applyTransform(this.rows, this.localConfig);
            const headers = Object.keys(transformedRows[0]);
            const escape = v => {
                const s = String(v == null ? '' : v);
                return (s.includes(',') || s.includes('"') || s.includes('\n'))
                    ? '"' + s.replace(/"/g, '""') + '"'
                    : s;
            };
            const csvRows = [
                headers.map(escape).join(','),
                ...transformedRows.map(row => headers.map(h => escape(row[h])).join(','))
            ];
            const blob = new Blob(['\uFEFF' + csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = (this.localConfig.title || 'donnees').replace(/[^a-zA-Z0-9-_]/g, '_') + '_brut.csv';
            link.click();
            URL.revokeObjectURL(url);
        },
        downloadCsv() {
            if (!this.rows || this.rows.length === 0) return;
            const cfg = this.localConfig;
            const transformedRows = applyTransform(this.rows, cfg);
            const { labels, datasets, isPie, useTimeScale } = buildChartData(transformedRows, cfg);

            let csvRows = [];
            if (useTimeScale) {
                const allX = new Set();
                datasets.forEach(ds => ds.data.forEach(pt => allX.add(pt.x)));
                const sortedX = Array.from(allX).sort();
                csvRows.push(['label', ...datasets.map(ds => ds.label || 'valeur')]);
                sortedX.forEach(x => {
                    const row = [x];
                    datasets.forEach(ds => {
                        const pt = ds.data.find(p => p.x === x);
                        row.push(pt !== undefined ? pt.y : '');
                    });
                    csvRows.push(row);
                });
            } else if (isPie || datasets.length === 1) {
                csvRows.push(['label', datasets[0]?.label || 'valeur']);
                labels.forEach((l, i) => csvRows.push([l, datasets[0].data[i]]));
            } else {
                csvRows.push(['label', ...datasets.map(ds => ds.label || 'valeur')]);
                labels.forEach((l, i) => csvRows.push([l, ...datasets.map(ds => ds.data[i])]));
            }

            const escape = v => {
                const s = String(v == null ? '' : v);
                return (s.includes(',') || s.includes('"') || s.includes('\n'))
                    ? '"' + s.replace(/"/g, '""') + '"'
                    : s;
            };
            const csvContent = csvRows.map(r => r.map(escape).join(',')).join('\n');
            const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = (cfg.title || 'chart-data').replace(/[^a-zA-Z0-9-_]/g, '_') + '.csv';
            link.click();
            URL.revokeObjectURL(url);
        },
        downloadImage() {
            if (!this.chartInstance) return;
            const canvas = this.$refs.canvas;
            if (!canvas) return;
            const link = document.createElement('a');
            link.href = canvas.toDataURL('image/png');
            link.download = (this.localConfig.title || 'graphique').replace(/[^a-zA-Z0-9-_]/g, '_') + '.png';
            link.click();
        },
        destroyChart() {
            if (this.chartInstance) {
                this.chartInstance.destroy();
                this.chartInstance = null;
            }
        },
        async renderChart() {
            await this.$nextTick();
            this.destroyChart();
            if (!this.rows || this.rows.length === 0) return;

            const canvas = this.$refs.canvas;
            if (!canvas) return;

            const cfg = this.localConfig;
            const transformedRows = applyTransform(this.rows, cfg);
            const { labels, datasets, isPie, isPolar, isRadar, isBubble, isColumn, isArea, useTimeScale, timeUnit } = buildChartData(transformedRows, cfg);

            this.pieLegendItems = [];
            if (isPie) {
                const self = this;
                this.chartInstance = new Chart(canvas, {
                    type: 'pie',
                    data: { labels, datasets },
                    options: {
                        responsive: true,
                        maintainAspectRatio: false,
                        interaction: { mode: 'nearest', intersect: true },
                        plugins: {
                            legend: { display: false },
                            title: { display: !!cfg.title, text: cfg.title || '' }
                        }
                    },
                    plugins: [{
                        id: 'htmlLegend',
                        afterUpdate(chart) {
                            const items = chart.options.plugins.legend.labels.generateLabels(chart);
                            self.pieLegendItems = items.map(item => ({
                                text:        item.text,
                                fillStyle:   item.fillStyle   || item.backgroundColor || '',
                                strokeStyle: item.strokeStyle || item.borderColor      || ''
                            }));
                        }
                    }]
                });
            } else if (isPolar) {
                const self = this;
                this.chartInstance = new Chart(canvas, {
                    type: 'polarArea',
                    data: { labels, datasets },
                    options: {
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                            legend: { display: false },
                            title: { display: !!cfg.title, text: cfg.title || '' }
                        }
                    },
                    plugins: [{
                        id: 'htmlLegend',
                        afterUpdate(chart) {
                            const items = chart.options.plugins.legend.labels.generateLabels(chart);
                            self.pieLegendItems = items.map(item => ({
                                text:        item.text,
                                fillStyle:   item.fillStyle   || item.backgroundColor || '',
                                strokeStyle: item.strokeStyle || item.borderColor      || ''
                            }));
                        }
                    }]
                });
            } else if (isRadar) {
                this.chartInstance = new Chart(canvas, {
                    type: 'radar',
                    data: { labels, datasets },
                    options: {
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                            legend: { display: datasets.length > 1 },
                            title: { display: !!cfg.title, text: cfg.title || '' }
                        }
                    }
                });
            } else if (isBubble) {
                this.chartInstance = new Chart(canvas, {
                    type: 'bubble',
                    data: { labels, datasets },
                    options: {
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                            legend: { display: datasets.length > 1 },
                            title: { display: !!cfg.title, text: cfg.title || '' },
                            tooltip: {
                                callbacks: {
                                    label(ctx) {
                                        const pt = ctx.raw;
                                        const prefix = ctx.dataset.label ? ctx.dataset.label + ': ' : '';
                                        const lbl = pt.pointLabel ? `${pt.pointLabel} — ` : '';
                                        return `${prefix}${lbl}(${pt.x}, ${pt.y}) r=${pt.r}`;
                                    }
                                }
                            }
                        },
                        scales: {
                            x: { beginAtZero: false },
                            y: { beginAtZero: false }
                        }
                    }
                });
            } else {
                this.chartInstance = new Chart(canvas, {
                    type: isColumn ? 'bar' : 'line',
                    data: { labels, datasets },
                    options: {
                        responsive: true,
                        maintainAspectRatio: false,
                        interaction: { mode: 'index', intersect: false },
                        plugins: {
                            legend: { display: datasets.length > 1 },
                            title: { display: !!cfg.title, text: cfg.title || '' },
                            zoom: {
                                zoom: {
                                    wheel: { enabled: true },
                                    pinch: { enabled: true },
                                    mode: 'x'
                                },
                                pan: {
                                    enabled: true,
                                    mode: 'x'
                                }
                            }
                        },
                        scales: {
                            x: useTimeScale
                                ? {
                                    type: 'time',
                                    time: { unit: timeUnit },
                                    ticks: { maxTicksLimit: 12, maxRotation: 45 },
                                    stacked: (isColumn || isArea) && !!cfg.stacked
                                  }
                                : {
                                    stacked: (isColumn || isArea) && !!cfg.stacked,
                                    ticks: {
                                        maxTicksLimit: 12,
                                        maxRotation: 45,
                                        callback(val, i, ticks) {
                                            const lbl = this.getLabelForValue(val);
                                            if (typeof lbl !== 'string') return lbl;
                                            return lbl.length > 20 ? lbl.slice(0, 50) + '…' : lbl;
                                        }
                                    }
                                  },
                            y: { beginAtZero: isColumn || isArea, stacked: (isColumn || isArea) && !!cfg.stacked }
                        }
                    }
                });
            }
        }
    }
};
</script>

<style scoped>
.chart-display-wrapper {
    margin-bottom: 12px;
}
.chart-display-toolbar {
    margin-bottom: 6px;
}
.chart-display-container {
    position: relative;
    height: 320px;
    margin-top: 8px;
}
.chart-display-container--line {
    height: 400px;
}
.chart-inline-config {
    background: #f8f9fa;
    max-height: 400px;
    overflow-y: auto;
}
.chart-pre-text {
    white-space: pre-wrap;
    margin-bottom: 8px;
    font-size: 0.92em;
}
.chart-html-legend {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 14px;
    margin-top: 8px;
    font-size: 0.82em;
}
.chart-legend-item {
    display: flex;
    align-items: center;
    gap: 5px;
    white-space: nowrap;
}
.chart-legend-swatch {
    display: inline-block;
    width: 12px;
    height: 12px;
    border-radius: 2px;
    border: 1px solid transparent;
    flex-shrink: 0;
}
</style>

